const DEFAULT_SECURITY_SETTINGS = {
  autoSubmit: true,
  rules: {
    tabSwitch: { enabled: true, max: 3 },
    windowBlur: { enabled: true, max: 3 },
    rightClick: { enabled: true, max: 5 },
    copyAttempt: { enabled: true, max: 5 },
    pasteAttempt: { enabled: true, max: 5 },
    devTools: { enabled: true, max: 10 },
    suspiciousTyping: { enabled: true, max: 3 }
  },
  suspiciousTyping: {
    charsPerSecond: 100,
    minChars: 10
  }
};

class QuizSecurity {
  constructor(settings = DEFAULT_SECURITY_SETTINGS) {
    this.violations = {
      tabSwitch: 0,
      windowBlur: 0,
      rightClick: 0,
      copyAttempt: 0,
      pasteAttempt: 0,
      devTools: 0,
      suspiciousTyping: 0
    };

    this.typingSpeed = [];
    this.lastTypingTime = Date.now();
    this.isMonitoring = false;
    this.callbacks = {};
    this.currentViolationReason = 'No violation detected';
    this.updateSettings(settings);

    this.initEventListeners();
  }

  updateSettings(settings = {}) {
    const incomingRules = settings?.rules || {};
    this.settings = {
      ...DEFAULT_SECURITY_SETTINGS,
      ...settings,
      rules: Object.keys(DEFAULT_SECURITY_SETTINGS.rules).reduce((acc, key) => {
        const defaultRule = DEFAULT_SECURITY_SETTINGS.rules[key];
        const incomingRule = incomingRules[key] || {};
        const max = Number(incomingRule.max);

        acc[key] = {
          enabled: typeof incomingRule.enabled === 'boolean' ? incomingRule.enabled : defaultRule.enabled,
          max: Number.isFinite(max) && max > 0 ? Math.round(max) : defaultRule.max
        };
        return acc;
      }, {}),
      suspiciousTyping: {
        ...DEFAULT_SECURITY_SETTINGS.suspiciousTyping,
        ...(settings?.suspiciousTyping || {})
      }
    };

    this.maxViolations = Object.fromEntries(
      Object.entries(this.settings.rules).map(([key, rule]) => [key, rule.max])
    );
  }

  isRuleEnabled(type) {
    return this.settings?.rules?.[type]?.enabled !== false;
  }

  isEditorClipboardAllowed(event) {
    const target = event?.target;
    const activeElement = document.activeElement;
    const isAllowedElement = (element) => (
      element?.closest?.('[data-allow-editor-clipboard="true"]') ||
      element?.matches?.('[data-allow-editor-clipboard="true"]')
    );

    return Boolean(isAllowedElement(target) || isAllowedElement(activeElement));
  }

  initEventListeners() {
    // Tab switching detection
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.isMonitoring) {
        this.logViolation('tabSwitch', 'Tab switched or minimized');
      }
    });

    // Window blur detection
    window.addEventListener('blur', () => {
      if (this.isMonitoring) {
        this.logViolation('windowBlur', 'Window lost focus');
      }
    });

    // Right-click detection
    document.addEventListener('contextmenu', (e) => {
      if (this.isMonitoring && this.isRuleEnabled('rightClick')) {
        e.preventDefault();
        this.logViolation('rightClick', 'Right-click attempted');
      }
    });

    // Copy detection
    document.addEventListener('copy', (e) => {
      if (this.isMonitoring && this.isRuleEnabled('copyAttempt')) {
        if (this.isEditorClipboardAllowed(e)) {
          return;
        }
        e.preventDefault();
        this.logViolation('copyAttempt', 'Copy attempted');
      }
    });

    // Cut detection
    document.addEventListener('cut', (e) => {
      if (this.isMonitoring && this.isRuleEnabled('copyAttempt')) {
        if (this.isEditorClipboardAllowed(e)) {
          return;
        }
        e.preventDefault();
        this.logViolation('copyAttempt', 'Cut attempted');
      }
    });

    // Paste detection
    document.addEventListener('paste', (e) => {
      if (this.isMonitoring && this.isRuleEnabled('pasteAttempt')) {
        if (this.isEditorClipboardAllowed(e)) {
          return;
        }
        e.preventDefault();
        this.logViolation('pasteAttempt', 'Paste attempted');
      }
    });

    // DevTools detection (weak signal)
    const devtools = {
      open: false,
      orientation: null
    };

    const threshold = 160;

    setInterval(() => {
      if (this.isMonitoring && this.isRuleEnabled('devTools')) {
        if (window.outerHeight - window.innerHeight > threshold ||
            window.outerWidth - window.innerWidth > threshold) {
          if (!devtools.open) {
            devtools.open = true;
            this.logViolation('devTools', 'DevTools possibly opened');
          }
        } else {
          devtools.open = false;
        }
      }
    }, 500);

    // Keyboard shortcuts detection
    document.addEventListener('keydown', (e) => {
      if (this.isMonitoring && this.isRuleEnabled('devTools')) {
        // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
        if (e.keyCode === 123 ||
            (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) ||
            (e.ctrlKey && e.keyCode === 85)) {
          e.preventDefault();
          this.logViolation('devTools', 'DevTools shortcut attempted');
        }
      }
    });
  }

  startMonitoring(callbacks = {}, settings = null) {
    if (settings) {
      this.updateSettings(settings);
    }

    this.isMonitoring = true;
    this.callbacks = callbacks;
    this.resetViolations();
    this.typingSpeed = [];
    this.lastTypingTime = Date.now();
  }

  stopMonitoring() {
    this.isMonitoring = false;
  }

  logViolation(type, message) {
    if (!this.isMonitoring || !this.isRuleEnabled(type)) {
      return;
    }

    this.violations[type]++;

    const max = this.maxViolations[type] || 1;
    const current = this.violations[type];

    this.currentViolationReason = `${message} (${current}/${max})`;

    console.warn(`Security Violation: ${message} (${current}/${max})`);

    if (current >= max) {
      if (this.settings.autoSubmit && this.callbacks.onAutoSubmit) {
        this.callbacks.onAutoSubmit(`Auto-submitted due to ${type} violations`, type, current, max);
        this.stopMonitoring();
      } else if (this.callbacks.onWarning) {
        this.callbacks.onWarning(`Warning limit reached: ${message}.`, type, current, max);
      }
    } else if (current >= max - 1) {
      if (this.callbacks.onWarning) {
        const consequence = this.settings.autoSubmit ? 'auto-submit' : 'reach the warning limit';
        this.callbacks.onWarning(`Warning: ${message}. One more violation will ${consequence}.`, type, current, max);
      }
    } else if (current === 1) {
      if (this.callbacks.onWarning) {
        this.callbacks.onWarning(`First warning: ${message}. Please avoid further violations.`, type, current, max);
      }
    } else if (this.callbacks.onInfo) {
      this.callbacks.onInfo(message, type, current, max);
    }
  }

  trackTyping(inputElement) {
    if (!this.isMonitoring || !this.isRuleEnabled('suspiciousTyping')) return undefined;

    let startTime = Date.now();
    let charCount = 0;

    const handleInput = () => {
      const currentTime = Date.now();
      const timeDiff = currentTime - startTime;

      if (timeDiff > 0) {
        const speed = charCount / (timeDiff / 1000);
        const threshold = Number(this.settings.suspiciousTyping?.charsPerSecond) || 100;
        const minChars = Number(this.settings.suspiciousTyping?.minChars) || 10;

        if (speed > threshold && charCount > minChars) {
          this.logViolation('suspiciousTyping', `Suspicious typing speed: ${speed.toFixed(0)} chars/sec`);
        }

        startTime = currentTime;
        charCount = 0;
      }
    };

    const handleKeyPress = () => {
      charCount++;
    };

    inputElement.addEventListener('input', handleInput);
    inputElement.addEventListener('keypress', handleKeyPress);

    return () => {
      inputElement.removeEventListener('input', handleInput);
      inputElement.removeEventListener('keypress', handleKeyPress);
    };
  }

  resetViolations() {
    Object.keys(this.violations).forEach(key => {
      this.violations[key] = 0;
    });
    this.currentViolationReason = 'No violation detected';
  }

  getViolationReport() {
    return {
      violations: { ...this.violations },
      maxViolations: { ...this.maxViolations },
      settings: this.settings,
      totalViolations: Object.values(this.violations).reduce((sum, count) => sum + count, 0)
    };
  }

  getCurrentViolationReason() {
    return this.currentViolationReason;
  }

  // Question randomization utilities
  static randomizeQuestions(questions) {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  static randomizeOptions(question) {
    if (question.type === 'radio' || question.type === 'checkbox') {
      const options = [...question.options];
      const correct = Array.isArray(question.correct) ? [...question.correct] : question.correct;

      const pairs = options.map((option) => ({
        option,
        isCorrect: Array.isArray(correct) ? correct.includes(option) : option === correct
      }));

      for (let i = pairs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }

      return {
        ...question,
        options: pairs.map(pair => pair.option),
        correct: Array.isArray(correct)
          ? pairs.filter(pair => pair.isCorrect).map(pair => pair.option)
          : pairs.find(pair => pair.isCorrect)?.option
      };
    }
    return question;
  }

  static randomizeTestCases(testCases) {
    return testCases.map(testCase => ({
      ...testCase,
      input: testCase.input.map(val => {
        if (typeof val === 'number') {
          const variation = Math.random() * 0.1 - 0.05;
          return Math.round(val * (1 + variation));
        }
        return val;
      })
    }));
  }
}

export default QuizSecurity;
